import type {
  ProfitGroupBy,
  ProfitMoney,
  ProfitReport,
  ProfitRow,
} from '@erp/shared';
import {
  AlertTriangle,
  Coins,
  Info,
  Percent,
  Timer,
  TrendingDown,
  TrendingUp,
  Truck,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Button as ShButton, Skeleton } from '../../components/shadcn/index.js';
import { Badge, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatDate, formatNumber } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

import { KpiCard } from './_shared/KpiCard.js';
import { PeriodPicker, rangeForPreset, type PeriodValue } from './_shared/PeriodPicker.js';
import { SaleCostingDialog, percent, type SaleRef } from './_shared/SaleCostingDialog.js';

const PAGE_SIZE = 50;

const GROUP_BYS: ProfitGroupBy[] = [
  'sale', 'day', 'item', 'channel', 'rep', 'party', 'terminal', 'cashier', 'branch',
];

interface WarehouseLite { id: string; name: string }

/**
 * Profit — what sales earned after what the goods cost.
 *
 * Two rules shape the whole page. Money is never added across currencies, so
 * the KPI strip describes ONE currency at a time and every row carries its own;
 * and a line the server could not cost is never quietly dropped — it gets the
 * amber card under the table, because revenue with no cost behind it is a job
 * for the owner, not an error to hide.
 */
export function ProfitReportPage(): JSX.Element {
  const { t, i18n } = useTranslation('reports');
  const { t: tc } = useTranslation();
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';

  const [period, setPeriod] = useState<PeriodValue>(() => {
    const r = rangeForPreset('last30');
    return { preset: 'last30', from: r.from, to: r.to };
  });
  const [groupBy, setGroupBy] = useState<ProfitGroupBy>('sale');
  const [offset, setOffset] = useState(0);
  // Branch lens: the select narrows every figure to one branch. Only offered
  // when the caller can see more than one (the endpoint is scope-narrowed).
  const [warehouses, setWarehouses] = useState<WarehouseLite[]>([]);
  const [warehouseId, setWarehouseId] = useState('all');
  // Which currency the KPI strip speaks. null = follow the default below.
  const [pickedCurrency, setPickedCurrency] = useState<string | null>(null);
  const [report, setReport] = useState<ProfitReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The sale whose line-by-line costing is open. Only reachable from groupBy=sale.
  const [openSale, setOpenSale] = useState<SaleRef | null>(null);

  useEffect(() => {
    // Costing a period is slow enough that a quick regroup can land its answer
    // after the next one — the flag keeps the older figures off the screen.
    let cancelled = false;
    setError(null);
    setReport(null);
    const params = new URLSearchParams({
      from: period.from,
      to: period.to,
      groupBy,
      limit: String(PAGE_SIZE),
      offset: String(offset),
    });
    if (warehouseId !== 'all') params.set('warehouseId', warehouseId);
    void api()
      .get<ProfitReport>(`/reports/profit?${params.toString()}`)
      .then((r) => { if (!cancelled) setReport(r.data); })
      .catch((e) => { if (!cancelled) setError(String((e as Error).message ?? e)); });
    return () => { cancelled = true; };
  }, [period.from, period.to, groupBy, offset, warehouseId]);

  useEffect(() => {
    void api().get<{ items: WarehouseLite[] }>('/warehouses')
      .then((r) => setWarehouses(r.data.items))
      .catch(() => setWarehouses([]));
  }, []);

  const locale = i18n.language;
  const money = (amount: string, currency: string, approximate = false): string =>
    formatMoney(amount, { locale, currency, approximate });
  const int = (n: number): string => formatNumber(n, { locale });

  const currencies = useMemo(
    () => (report ? report.totalsByCurrency.map((x) => x.currencyCode) : []),
    [report],
  );
  const activeCurrency = pickedCurrency && currencies.includes(pickedCurrency)
    ? pickedCurrency
    : currencies.includes(baseCurrency) ? baseCurrency : currencies[0] ?? baseCurrency;
  const totals = report?.totalsByCurrency.find((x) => x.currencyCode === activeCurrency) ?? null;
  // Takings with no cost behind them, in the currency on screen. Null when there
  // is no gap, so the KPI hints fall back to their ordinary caption.
  const gap = totals && Number(totals.excludedRevenue) > 0 ? totals.excludedRevenue : null;

  // Period and grouping both change what a page number means, so both send the
  // reader back to the first page rather than into an offset that no longer exists.
  const onPeriodChange = (next: PeriodValue): void => {
    setPeriod(next);
    setOffset(0);
  };
  const onGroupByChange = (next: ProfitGroupBy): void => {
    setGroupBy(next);
    setOffset(0);
  };

  const openRow = (row: ProfitRow): void => {
    if (row.docType && row.docId) setOpenSale({ docType: row.docType, docId: row.docId });
  };

  // A sale row opens its own costing rather than the document: the button keeps
  // that reachable from the keyboard, the row click is the mouse shortcut.
  const rowLabel = (row: ProfitRow): ReactNode => {
    if (groupBy === 'day') return formatDate(row.label, { locale });
    if (groupBy === 'channel') return t(`profit.channel.${row.label}`);
    if (groupBy !== 'sale' || !row.docType || !row.docId) return row.label;
    return (
      <div className="min-w-0">
        <button
          type="button"
          onClick={() => openRow(row)}
          className="font-medium text-brand underline-offset-4 hover:underline"
        >
          {row.label}
        </button>
        {row.docDate && (
          <div className="text-xs text-fg-muted">{formatDate(row.docDate, { locale })}</div>
        )}
      </div>
    );
  };

  const columns: DataTableColumn<ProfitRow>[] = [
    {
      id: 'label',
      header: t(`profit.group.${groupBy}`),
      cell: (row) => rowLabel(row),
    },
    {
      id: 'currency',
      header: t('profit.columns.currency'),
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (row) => row.currencyCode,
    },
    {
      id: 'revenue',
      header: t('profit.columns.revenue'),
      align: 'end',
      cellClassName: 'font-mono tabular-nums',
      cell: (row) => money(row.revenue, row.currencyCode),
    },
    {
      id: 'cost',
      header: t('profit.columns.cost'),
      align: 'end',
      cellClassName: 'font-mono tabular-nums',
      cell: (row) => (
        <span title={row.isEstimate ? t('profit.estimateRow') : undefined}>
          {money(row.cost, row.currencyCode, row.isEstimate)}
        </span>
      ),
    },
    {
      id: 'profit',
      header: t('profit.columns.profit'),
      align: 'end',
      cellClassName: 'font-mono font-semibold tabular-nums',
      cell: (row) => (
        <span className={Number(row.profit) < 0 ? 'text-danger' : 'text-success'}>
          {money(row.profit, row.currencyCode)}
        </span>
      ),
    },
    {
      id: 'margin',
      header: t('profit.columns.margin'),
      align: 'end',
      cellClassName: 'font-mono tabular-nums',
      cell: (row) => (row.marginPercent === null ? '—' : percent(row.marginPercent, locale)),
    },
    {
      id: 'volume',
      header: t('profit.columns.volume'),
      align: 'end',
      cellClassName: 'text-xs text-fg-muted',
      cell: (row) => t('profit.volume', { lines: int(row.lineCount), docs: int(row.docCount) }),
    },
    {
      id: 'notes',
      header: t('profit.columns.notes'),
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.hasUninvoiced && (
            <Badge tone="info" title={t('profit.tags.uninvoicedHint')}>
              {t('profit.tags.uninvoiced')}
            </Badge>
          )}
          {row.excludedLineCount > 0 && (
            <Badge
              tone="warning"
              title={t('profit.tags.noCostHint', {
                amount: money(row.excludedRevenue, row.currencyCode),
              })}
            >
              {t('profit.tags.noCost', { n: int(row.excludedLineCount) })}
            </Badge>
          )}
        </div>
      ),
    },
  ];

  const shownFrom = report && report.total > 0 ? offset + 1 : 0;
  const shownTo = report ? offset + report.rows.length : 0;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('profit.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('profit.description')}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <PeriodPicker value={period} onChange={onPeriodChange} />
          {warehouses.length > 1 && (
            <label className="flex flex-col gap-1 text-xs text-fg-muted">
              <span>{tc('filters.branch', { defaultValue: 'Branch' })}</span>
              <select
                className="h-8 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
                value={warehouseId}
                onChange={(e) => { setWarehouseId(e.target.value); setOffset(0); }}
              >
                <option value="all">{tc('filters.allBranches', { defaultValue: 'All branches' })}</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </label>
          )}
          <label className="flex flex-col gap-1 text-xs text-fg-muted">
            <span>{t('profit.groupBy')}</span>
            <select
              className="h-8 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
              value={groupBy}
              onChange={(e) => onGroupByChange(e.target.value as ProfitGroupBy)}
            >
              {GROUP_BYS.map((g) => (
                <option key={g} value={g}>{t(`profit.group.${g}`)}</option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {error ? (
        <Card className="border-danger/40 bg-danger/5 p-4">
          <div className="flex items-start gap-2 text-sm text-danger" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
            <div className="flex-1">
              <p className="font-medium">{t('profit.loadFailed')}</p>
              <p className="mt-0.5 text-xs text-danger/80">{error}</p>
            </div>
          </div>
        </Card>
      ) : (
        <>
          {currencies.length > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('profit.currencyFilter')}>
                {currencies.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setPickedCurrency(c)}
                    aria-pressed={c === activeCurrency}
                    className={`rounded-full border px-2.5 py-0.5 text-xs font-medium tabular-nums transition ${
                      c === activeCurrency
                        ? 'border-brand bg-brand/10 text-brand'
                        : 'border-border text-fg-muted hover:bg-bg-subtle'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <span className="text-xs text-fg-muted">{t('profit.currencyHint')}</span>
            </div>
          )}

          {/* Revenue is everything taken; cost, profit and margin describe only the
              part a cost was found for. `gap` names the difference so the strip
              can never look like it added up to something it did not. */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <KpiCard
              tone="success"
              icon={TrendingUp}
              label={t('profit.kpi.revenue')}
              value={totals ? money(totals.revenue, activeCurrency) : '—'}
              hint={
                gap
                  ? t('profit.kpi.revenueGapHint', { amount: money(gap, activeCurrency) })
                  : totals ? t('profit.kpi.docsHint', { n: int(totals.docCount) }) : undefined
              }
              loading={!report}
            />
            <KpiCard
              tone="warning"
              icon={TrendingDown}
              label={t('profit.kpi.cost')}
              value={totals ? money(totals.cost, activeCurrency) : '—'}
              hint={totals ? t('profit.kpi.linesHint', { n: int(totals.lineCount) }) : undefined}
              loading={!report}
            />
            <KpiCard
              tone={totals && Number(totals.profit) < 0 ? 'danger' : 'brand'}
              icon={Coins}
              label={t('profit.kpi.profit')}
              value={totals ? money(totals.profit, activeCurrency) : '—'}
              loading={!report}
            />
            <KpiCard
              tone="info"
              icon={Percent}
              label={t('profit.kpi.margin')}
              value={totals?.marginPercent != null ? percent(totals.marginPercent, locale) : '—'}
              hint={
                gap && totals
                  ? t('profit.kpi.marginHint', { amount: money(totals.costedRevenue, activeCurrency) })
                  : undefined
              }
              loading={!report}
            />
          </div>

          {report?.isEstimate && (
            <p className="flex items-start gap-2 text-xs text-fg-muted">
              <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden />
              {t('profit.estimateNote')}
            </p>
          )}

          {!report ? (
            <Card className="p-6"><Skeleton className="h-64 w-full" /></Card>
          ) : report.rows.length === 0 ? (
            <EmptyState
              icon={<Coins className="h-8 w-8" aria-hidden />}
              title={t('profit.empty')}
              description={t('profit.emptyDescription')}
            />
          ) : (
            <Card className="overflow-hidden p-0">
              {/* Server-paged, so the table neither sorts nor searches: both would
                  claim to work on the whole period and only touch this page. */}
              <DataTable
                columns={columns}
                data={report.rows}
                rowKey={(row) => row.key}
                paginated={false}
                searchable={false}
                onRowClick={groupBy === 'sale' ? openRow : undefined}
              />
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-3 text-sm text-fg-muted">
                <span>
                  {t('profit.showing', {
                    from: int(shownFrom),
                    to: int(shownTo),
                    total: int(report.total),
                  })}
                </span>
                <div className="flex gap-2">
                  <ShButton
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                    disabled={offset === 0}
                  >
                    {t('profit.prev')}
                  </ShButton>
                  <ShButton
                    variant="outline"
                    size="sm"
                    onClick={() => setOffset((o) => o + PAGE_SIZE)}
                    disabled={shownTo >= report.total}
                  >
                    {t('profit.next')}
                  </ShButton>
                </div>
              </div>
            </Card>
          )}

          {report && report.excluded.lineCount > 0 && (
            <Card className="border-warning/40 bg-warning/5 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold text-warning">{t('profit.excluded.title')}</h2>
                  <p className="mt-0.5 text-xs text-fg-muted">{t('profit.excluded.subtitle')}</p>

                  <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                      <dt className="text-xs text-fg-muted">{t('profit.excluded.linesLabel')}</dt>
                      <dd className="font-mono text-lg font-semibold tabular-nums">
                        {int(report.excluded.lineCount)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-fg-muted">{t('profit.excluded.revenue')}</dt>
                      <dd className="mt-0.5">
                        <MoneyList
                          list={report.excluded.revenue}
                          locale={locale}
                          className="font-mono text-sm font-semibold tabular-nums"
                        />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-fg-muted">{t('profit.excluded.reasons')}</dt>
                      <dd className="mt-1 flex flex-wrap gap-1.5">
                        {report.excluded.reasons.map((r) => (
                          <Badge key={r.reason} tone="warning">
                            {t(`profit.excluded.reason.${r.reason}`)} · {int(r.lineCount)}
                          </Badge>
                        ))}
                      </dd>
                    </div>
                  </dl>

                  {report.excluded.topItems.length > 0 && (
                    <>
                      <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
                        {t('profit.excluded.topItems')}
                      </h3>
                      <ul className="mt-2 flex flex-col divide-y divide-border-subtle rounded-md border border-border-subtle bg-bg/40">
                        {report.excluded.topItems.map((it) => (
                          <li
                            key={it.itemId}
                            className="flex flex-wrap items-center justify-between gap-3 px-3 py-2"
                          >
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium">{it.name}</div>
                              <div className="font-mono text-xs text-fg-muted" dir="ltr">{it.code}</div>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                              <Badge tone="warning">{t(`profit.excluded.reason.${it.reason}`)}</Badge>
                              <span className="text-xs text-fg-muted">
                                {t('profit.excluded.itemLines', { n: int(it.lineCount) })}
                              </span>
                              <MoneyList
                                list={it.revenue}
                                locale={locale}
                                className="font-mono text-sm tabular-nums"
                              />
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </Card>
          )}

          {report && report.provisional.orderCount > 0 && (
            <Card className="p-4">
              <div className="flex items-start gap-2">
                <Timer className="mt-0.5 h-4 w-4 flex-shrink-0 text-info" aria-hidden />
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold">{t('profit.provisional.title')}</h2>
                  <p className="mt-0.5 text-xs text-fg-muted">{t('profit.provisional.subtitle')}</p>
                  <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div>
                      <dt className="text-xs text-fg-muted">{t('profit.provisional.orders')}</dt>
                      <dd className="font-mono text-lg font-semibold tabular-nums">
                        {int(report.provisional.orderCount)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-fg-muted">{t('profit.provisional.revenue')}</dt>
                      <dd className="mt-0.5">
                        <MoneyList
                          list={report.provisional.revenue}
                          locale={locale}
                          className="font-mono text-sm tabular-nums"
                        />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-fg-muted">{t('profit.provisional.profit')}</dt>
                      <dd className="mt-0.5">
                        <MoneyList
                          list={report.provisional.profit}
                          locale={locale}
                          className="font-mono text-sm tabular-nums"
                        />
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </Card>
          )}

          {report && report.nonProductRevenue.length > 0 && (
            <Card className="p-4">
              <div className="flex items-start gap-2">
                <Truck className="mt-0.5 h-4 w-4 flex-shrink-0 text-fg-muted" aria-hidden />
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold">{t('profit.nonProduct.title')}</h2>
                  <p className="mt-0.5 text-xs text-fg-muted">{t('profit.nonProduct.subtitle')}</p>
                  <div className="mt-2">
                    <MoneyList
                      list={report.nonProductRevenue}
                      locale={locale}
                      className="font-mono text-sm font-semibold tabular-nums"
                    />
                  </div>
                </div>
              </div>
            </Card>
          )}
        </>
      )}

      <SaleCostingDialog sale={openSale} onClose={() => setOpenSale(null)} />
    </div>
  );
}

interface MoneyListProps {
  list: ProfitMoney[];
  locale: string;
  className?: string;
}

/** Amounts in several currencies at once, side by side — adding them would lie. */
function MoneyList({ list, locale, className }: MoneyListProps): JSX.Element {
  if (list.length === 0) return <span className="text-sm text-fg-muted">—</span>;
  return (
    <span className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
      {list.map((m) => (
        <span key={m.currencyCode} className={className}>
          {formatMoney(m.amount, { locale, currency: m.currencyCode })}
        </span>
      ))}
    </span>
  );
}
